import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { registerUser } from "../services/api";
import { postLoginRedirect } from "../auth/postLoginRedirect";
import { api } from "../api/clientInstance";
import COUNTRIES from "../data/countries";
import "./AuthForm.css";

export default function SignupForm({ onClose }) {
  const { login }         = useAuth();
  const navigate          = useNavigate();
  const [form, setForm]       = useState({ username: "", email: "", password: "", country: "", cupCode: "" });
  const [error, setError]     = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [cupName, setCupName]   = useState(null);
  const [cupError, setCupError] = useState("");
  const usernameRef = useRef(null);

  useEffect(() => { usernameRef.current?.focus(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    const code = form.cupCode.trim().toUpperCase();
    if (code.length < 6) { setCupName(null); setCupError(""); return; }
    api(`/cup/validate/${code}`).then((r) => { setCupName(r.name); setCupError(""); }).catch(() => { setCupName(null); setCupError("Invalid or expired cup code"); });
  }, [form.cupCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.cupCode && cupError) return;
    try {
      const data = await registerUser(form.email.trim() || null, form.password.trim(), form.username.trim(), null, form.country || null, form.cupCode || null);
      await login(data.access_token);
      onClose?.();
      await postLoginRedirect(navigate);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Registration failed";
      setError(msg === "CUP_FULL" ? "This cup is already full" : msg);
    }
  };

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-title">Sign Up</h2>
      {error && <p className="auth-error">{error}</p>}
      <input
        ref={usernameRef}
        type="text"
        placeholder="Username"
        name="new-username"
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-protonpass-ignore="true"
        data-form-type="other"
        value={form.username}
        onChange={update("username")}
        required
        minLength={3}
        maxLength={20}
      />
      <input type="email" placeholder="Email (optional)" autoComplete="email" value={form.email} onChange={update("email")} />
      {!form.email && <p className="auth-warning">Without an email you won't be able to recover your password.</p>}
      <div className="auth-pw-wrap">
        <input type={showPw ? "text" : "password"} placeholder="Password" autoComplete="new-password" value={form.password} onChange={update("password")} required minLength={8} />
        <button type="button" className="auth-pw-toggle" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
          {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
      <select className="auth-select" value={form.country} onChange={update("country")}>
        <option value="">Select your country…</option>
        <option value="XX">Neutral / Prefer not to say</option>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{c.name}</option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Cup code (optional)"
        value={form.cupCode}
        onChange={update("cupCode")}
        maxLength={6}
      />
      {cupName  && <p className="auth-cup-ok">Joining cup: <strong>{cupName}</strong></p>}
      {cupError && <p className="auth-error">{cupError}</p>}
      <div className="auth-actions">
        <button type="submit" disabled={!!(form.cupCode && cupError)}>Sign Up</button>
        <button type="button" className="auth-cancel" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
